'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, ArrowRight, Search, Briefcase, Cpu, Calendar } from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

type ApiCategory = {
  id: string;
  slug: string;
  name: string;
};

type ApiProject = {
  id: string;
  title: string;
  slug: string;
  description: string;
  techStack: string[];
  images?: string[];
  coverImage?: string | null;
  liveUrl?: string | null;
  githubUrl?: string | null;
  featured: boolean;
  category: string;
};

const GRADIENTS = [
  'linear-gradient(135deg, #6C63FF 0%, #9F7AEA 50%, #FF6584 100%)',
  'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #EC4899 100%)',
  'linear-gradient(135deg, #06B6D4 0%, #6C63FF 50%, #8B5CF6 100%)',
  'linear-gradient(135deg, #FF6584 0%, #F97316 50%, #FACC15 100%)',
  'linear-gradient(135deg, #0EA5E9 0%, #6366F1 50%, #A855F7 100%)',
  'linear-gradient(135deg, #10B981 0%, #14B8A6 50%, #6C63FF 100%)',
  'linear-gradient(135deg, #F43F5E 0%, #8B5CF6 50%, #3B82F6 100%)',
  'linear-gradient(135deg, #22D3EE 0%, #14B8A6 50%, #22C55E 100%)',
  'linear-gradient(135deg, #EAB308 0%, #F97316 50%, #EF4444 100%)',
];

function getInitials(title: string): string {
  const words = title
    .split(/\s+/)
    .filter((w) => /[a-zA-Z0-9]/.test(w))
    .filter((w) => !['a', 'an', 'the', 'of', 'and', '&'].includes(w.toLowerCase()));
  const letters = words.slice(0, 2).map((w) => w[0].toUpperCase());
  if (letters.length === 0) return title.slice(0, 2).toUpperCase();
  if (letters.length === 1) return letters[0] + (words[0][1] ?? '').toUpperCase();
  return letters.join('');
}

function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '…';
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const gridContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } },
};

export default function ProjectsPage() {
  const [filter, setFilter] = useState<string>('All');
  const [query, setQuery] = useState('');
  const projects = useSiteData<ApiProject[]>('projects', '/api/projects', []);
  const categories = useSiteData<ApiCategory[]>(
    'categories-project',
    '/api/categories?type=project',
    [],
  );
  const FILTERS: string[] = useMemo(
    () => ['All', ...categories.map((c) => c.name)],
    [categories],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      const techStack = (p.techStack as string[]) ?? [];
      // Match against either category name or slug for flexibility with older records
      const tabMatch =
        filter === 'All' ||
        p.category === filter ||
        p.category?.toLowerCase() === filter.toLowerCase();

      if (!tabMatch) return false;

      if (q.length === 0) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        techStack.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [filter, query, projects]);

  const totalProjects = projects.length;
  const totalTech = useMemo(() => {
    const s = new Set<string>();
    projects.forEach((p) => ((p.techStack as string[]) ?? []).forEach((t) => s.add(t)));
    return s.size;
  }, [projects]);
  const years = 5;

  return (
    <motion.div
      className="projects-page-wrap"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Hero banner */}
      <section className="projects-page-hero">
        <div className="projects-page-hero-blob projects-page-hero-blob-1" />
        <div className="projects-page-hero-blob projects-page-hero-blob-2" />
        <div className="projects-page-hero-grid-bg" />

        <div className="section-container">
          <motion.div
            className="projects-page-hero-inner"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <span className="section-label">My Work</span>
            <h1 className="projects-page-hero-title">
              Selected <span className="projects-page-hero-title-grad">Projects</span>
            </h1>
            <p className="projects-page-hero-subtitle">
              A curated collection of products, platforms, and experiments — built with modern
              frameworks, thoughtful UX, and an obsession for details.
            </p>

            {/* Search */}
            <div className="projects-page-search-wrap">
              <Search size={18} className="projects-page-search-icon" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title, description, or technology…"
                className="projects-page-search-input"
                aria-label="Search projects"
              />
              {query.length > 0 && (
                <button
                  type="button"
                  className="projects-page-search-clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="projects-page-body">
        <div className="section-container">
          {/* Filter tabs */}
          <motion.div
            className="projects-page-filters"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {FILTERS.map((f) => {
              const count =
                f === 'All'
                  ? projects.length
                  : projects.filter(
                      (p) =>
                        p.category === f ||
                        p.category?.toLowerCase() === f.toLowerCase(),
                    ).length;

              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`projects-page-filter ${
                    filter === f ? 'projects-page-filter-active' : ''
                  }`}
                  aria-pressed={filter === f}
                >
                  <span className="projects-page-filter-label">{f}</span>
                  <span className="projects-page-filter-count">{count}</span>
                </button>
              );
            })}
          </motion.div>

          {/* Grid with AnimatePresence */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${filter}-${query}`}
              className="projects-page-grid"
              variants={gridContainer}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {filtered.length === 0 && (
                <div className="projects-page-empty">
                  <div className="projects-page-empty-icon">
                    <Search size={24} />
                  </div>
                  <h3 className="projects-page-empty-title">
                    {projects.length === 0 ? 'Projects coming soon' : 'No matches found'}
                  </h3>
                  <p className="projects-page-empty-text">
                    {projects.length === 0
                      ? 'New work is in the pipeline — check back shortly.'
                      : 'Try a different filter or search term.'}
                  </p>
                </div>
              )}

              {filtered.map((project, i) => {
                const gradient = GRADIENTS[i % GRADIENTS.length];
                const initials = getInitials(project.title);
                const techStack = (project.techStack as string[]) ?? [];
                const cover =
                  project.coverImage ||
                  (project.images && project.images.length > 0 ? project.images[0] : null);
                return (
                  <motion.article
                    key={project.id}
                    className="projects-page-card"
                    variants={cardVariant}
                    whileHover={{ y: -8 }}
                  >
                    {/* Header */}
                    <div
                      className="projects-page-card-header"
                      style={{ background: gradient }}
                    >
                      {cover ? (
                        <OptimizedImage
                          src={cover}
                          alt={project.title}
                          className="projects-page-card-img"
                          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                          loading="lazy"
                        />
                      ) : (
                        <>
                          <div className="projects-page-card-pattern" />
                          <span className="projects-page-card-initials">{initials}</span>
                        </>
                      )}
                      <span className="projects-page-card-badge">{project.category}</span>

                      {/* Hover overlay */}
                      <div className="projects-page-card-overlay">
                        {project.liveUrl && (
                          <a
                            href={project.liveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="projects-page-card-overlay-btn projects-page-card-overlay-live"
                            aria-label={`View ${project.title} live`}
                          >
                            <ExternalLink size={16} />
                            <span>View Live</span>
                          </a>
                        )}
                        {project.githubUrl && (
                          <a
                            href={project.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="projects-page-card-overlay-btn projects-page-card-overlay-gh"
                            aria-label={`View ${project.title} on GitHub`}
                          >
                            <FaGithub size={16} />
                            <span>GitHub</span>
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="projects-page-card-body">
                      <Link
                        href={`/projects/${project.slug}`}
                        className="projects-page-card-title-link"
                      >
                        <h3 className="projects-page-card-title">{project.title}</h3>
                      </Link>
                      <p className="projects-page-card-description">
                        {truncateText(project.description, 120)}
                      </p>

                      <div className="projects-page-card-tech">
                        {techStack.slice(0, 4).map((tech) => (
                          <span key={tech} className="projects-page-card-pill">
                            {tech}
                          </span>
                        ))}
                        {techStack.length > 4 && (
                          <span className="projects-page-card-pill projects-page-card-pill-more">
                            +{techStack.length - 4} more
                          </span>
                        )}
                      </div>

                      <Link
                        href={`/projects/${project.slug}`}
                        className="projects-page-card-cta"
                      >
                        View Case Study
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </motion.article>
                );
              })}
            </motion.div>
          </AnimatePresence>

          {/* Stats strip */}
          <motion.div
            className="projects-page-stats"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <div className="projects-page-stat">
              <div className="projects-page-stat-icon">
                <Briefcase size={22} />
              </div>
              <div className="projects-page-stat-text">
                <span className="projects-page-stat-value">{totalProjects}+</span>
                <span className="projects-page-stat-label">Projects Delivered</span>
              </div>
            </div>

            <div className="projects-page-stat-divider" />

            <div className="projects-page-stat">
              <div className="projects-page-stat-icon">
                <Cpu size={22} />
              </div>
              <div className="projects-page-stat-text">
                <span className="projects-page-stat-value">{totalTech}+</span>
                <span className="projects-page-stat-label">Technologies Used</span>
              </div>
            </div>

            <div className="projects-page-stat-divider" />

            <div className="projects-page-stat">
              <div className="projects-page-stat-icon">
                <Calendar size={22} />
              </div>
              <div className="projects-page-stat-text">
                <span className="projects-page-stat-value">{years}+</span>
                <span className="projects-page-stat-label">Years of Experience</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
